import React, { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  Badge
} from '@mui/material';
import { NavigateNext } from '@mui/icons-material';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { OutletContextType } from '../root';
import { managerPortalInitialize } from '../../api/api';

/**
 * テーブルのレコードの型
 */
export type MngTKGEvent = {
  event_id: string,
  event_name: string,
  event_date: string,
  b_start_time: string,
  b_end_time: string,
  d_start_time: string,
  d_end_time: string,
  b_entry_fee: string,
  d_entry_fee: string,
  participants_num: string,
  capacity_num: string,
  arena_name: string,
  restaurant: string,
  restaurant_url: string,
  public_flag: string,
  cash_in: string,
  cash_out: string,
  cash_flow: string,
  unapproved_number: string
};

/**
 * 体育館情報
 */
type ArenaInfo = {
  id: string,
  name: string
};
  
export const ManagerEventList = () => {
    const navigate = useNavigate();
    const { userId } = useOutletContext<OutletContextType>();
    const [events_data, setEventsData] = useState<MngTKGEvent[]>([]); // イベントデータ
    const [filteredData, setFilteredData] = useState<MngTKGEvent[]>([]); // フィルタリングされたデータ
    const [arenaOptions, setArenaOptions] = useState<ArenaInfo[]>([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [totalCashFlow, setTotalCashFlow] = useState(0);
    const [filters, setFilters] = useState({
      event_date_from: '', // 開催日 From
      event_date_to: '',   // 開催日 To
      arena_name: '',
      restaurant: '',
      drinking: false,
    });
  
    const [sortConfig, setSortConfig] = useState({
      key: 'event_date',
      direction: 'asc',
    });

    // レスポンシブ対応
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // モバイル用のスタイル適用
  
    // 初期表示処理を行う
    useEffect(() => {
      const fetchData = async () => {
        try {
          const response = await managerPortalInitialize(userId);
          const resultList: MngTKGEvent[] = [];
          for (const details of response.data.tkgReserve0200DetailsObjectLot.tkgReserve0200DetailsObjectList) {
            const event: MngTKGEvent = {
                event_id: details.eventId,
                event_name: details.eventName,
                event_date: details.eventDate,
                b_start_time: details.bStartTime,
                b_end_time: details.bEndTime,
                d_start_time: details.dStartTime,
                d_end_time: details.dEndTime,
                b_entry_fee: details.bEntryFee,
                d_entry_fee: details.dEntryFee,
                participants_num: details.participantsNum,
                capacity_num: details.capacityNum,
                arena_name: details.arenaName,
                restaurant: details.restaurant,
                restaurant_url: details.restaurantUrl,
                public_flag: details.publicFlag,
                cash_in: details.cashIn,
                cash_out: details.cashOut,  
                cash_flow: (parseInt(details.cashIn, 10) - parseInt(details.cashOut, 10)).toString(),
                unapproved_number: details.unapprovedNumber
            };
            resultList.push(event);
          }

          // 昇順にソート(eventDateを辞書順ソート)
          resultList.sort((a, b) => (a.event_date > b.event_date ? 1 : -1));

          setEventsData(resultList);
          setFilteredData(resultList);

          const newArenaInfo = response.data.arenaList.map((arenaEntity: any) => ({
            id: arenaEntity.arenaId,
            name: arenaEntity.arenaName,
          }));
          setArenaOptions(newArenaInfo);  
        } catch (error) {
          console.error("Error fetching data:", error);
        }
      };
      fetchData();
    }, [userId]);

    // ソート処理
    const handleSort = (key: string) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
          direction = 'desc'; // 既に昇順なら降順に切り替え
        }
        setSortConfig({ key, direction });
      
        const sortedData = [...filteredData].sort((a, b) => {
          if (key === 'event_date' || key === 'cash_flow') {
            const aValue = key === 'event_date' ? new Date(a[key as keyof typeof a]).getTime() : parseInt(a[key as keyof typeof a], 10);
            const bValue = key === 'event_date' ? new Date(b[key as keyof typeof b]).getTime() : parseInt(b[key as keyof typeof b], 10);
            return direction === 'asc' ? aValue - bValue : bValue - aValue;
          } else {
            const aValueStr = a[key as keyof typeof a] ? a[key as keyof typeof a].toString().toLowerCase() : '';
            const bValueStr = b[key as keyof typeof b] ? b[key as keyof typeof b].toString().toLowerCase() : '';
            return direction === 'asc' ? aValueStr.localeCompare(bValueStr) : bValueStr.localeCompare(aValueStr);
          }
        });
        setFilteredData(sortedData);
      };

    // 検索処理
    const handleSearch = () => {
      const filtered = events_data.filter((event) => {
        const eventDate = new Date(event.event_date);
        const fromDate = new Date(filters.event_date_from);
        const toDate = new Date(filters.event_date_to);
        
        const isDateInRange = (
          (filters.event_date_from && eventDate >= fromDate) ||
          !filters.event_date_from
        ) && (
          (filters.event_date_to && eventDate <= toDate) ||
          !filters.event_date_to
        );

        return (
          isDateInRange &&
          (filters.arena_name ? event.arena_name === filters.arena_name : true) &&
          (filters.restaurant ? event.restaurant.includes(filters.restaurant) : true) &&
          (filters.drinking ? (event.d_start_time !== null && event.d_start_time !== '') : true)
        );
      });
      setFilteredData(filtered);
    };
  
    // 検索条件をクリアする関数
    const handleClearFilters = () => {
      setFilters({
        event_date_from: '',
        event_date_to: '',
        arena_name: '',
        restaurant: '',
        drinking: false,
      });
      setFilteredData(events_data); // 初期データにリセット
    };
  
    // 収支計算処理
    const handleShowCashFlow = () => {
      let total = 0;
      filteredData.forEach((event) => {
        total += parseInt(event.cash_flow, 10);
      });
      setTotalCashFlow(total);
      setOpenDialog(true);
    };
  
    return (
      <Box sx={{ padding: 2, paddingTop: '10vh' }}>
        <Button
            color="inherit"
            variant="contained"
            onClick={() => navigate("/portal", { state: {user_id: userId} })}
            sx={{
                padding: "12px 24px",
                borderRadius: "8px",
                margin: "16px 0",
                fontSize: "16px",
                width: isMobile ? "60%" : "20%", 
                transition: "all 0.3s ease",
                '&:active': {
                transform: 'scale(0.98)',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                },
                '&:hover': {
                boxShadow: '0 6px 12px rgba(0, 0, 0, 0.2)',
                }
            }}
        >
            ユーザーポータルへ
        </Button>
        <Typography variant="h4" gutterBottom>
          イベント一覧
        </Typography>
  
        {/* 検索エリア */}
        <Box
        sx={{
            display: isMobile ? '' : 'flex',
            gap: 2,
            marginBottom: 2,
            backgroundColor: '#FFDAB9',
            padding: '20px',
            width: isMobile ? "60%" : "80%", 
            borderRadius: '8px',
            boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
        }}
        >
        {/* 開催日 From */}
        <TextField
            label="開催日 From"
            type="date"
            value={filters.event_date_from}
            onChange={(e) => setFilters({ ...filters, event_date_from: e.target.value })}
            InputLabelProps={{
            shrink: true,
            }}
            sx={{ width: '180px', backgroundColor: 'white' }}
        />
        
        {/* 開催日 To */}
        <TextField
            label="開催日 To"
            type="date"
            value={filters.event_date_to}
            onChange={(e) => setFilters({ ...filters, event_date_to: e.target.value })}
            InputLabelProps={{
            shrink: true,
            }}
            sx={{ width: '180px', backgroundColor: 'white' }}
        />

        {/* 体育館 */}
        <FormControl sx={{ minWidth: 120, marginRight: "20px" }}>
            <InputLabel>体育館</InputLabel>
            <Select
            value={filters.arena_name}
            sx={{ width: '180px', backgroundColor: 'white' }}
            onChange={(e) => setFilters({ ...filters, arena_name: e.target.value })}
            >
            <MenuItem value="">すべて</MenuItem>
            {/* フィルタリング可能な体育館のリスト */}
            {arenaOptions?.map((arena) => (
                <MenuItem key={arena.id} value={arena.name}>
                    {arena.name}
                </MenuItem>
            ))}
            </Select>
        </FormControl>

        {/* レストラン */}
        <TextField
            label="飲み会場"
            value={filters.restaurant}
            onChange={(e) => setFilters({ ...filters, restaurant: e.target.value })}
            sx={{ width: '180px', backgroundColor: 'white' }}
        />

        {/* 飲み会 */}
        <FormControlLabel
            control={
            <Checkbox
                checked={filters.drinking}
                onChange={(e) => setFilters({ ...filters, drinking: e.target.checked })}
            />
            }
            label="飲み会あり"
            sx={{
              '& .MuiFormControlLabel-label': {
                whiteSpace: 'nowrap'
              }
            }}
        />

        <Button onClick={handleSearch} variant="contained" color="primary" sx={{ width: '180px' }}>
          検索
        </Button>
        <Button
            onClick={handleClearFilters}
            variant="outlined"
            color="primary"
            sx={{ width: '180px' }}
        >
            クリア
        </Button>
        </Box>

        {/* 収支合計表示ボタン */}
        <Button
          variant="contained"
          color="primary"
          sx={{ margin: 1 }}
          onClick={handleShowCashFlow}
        >
          収支合計表示
        </Button>

        {/* イベント新規登録画面遷移ボタン */}
        <Button
          variant="contained"
          color="primary"
          sx={{ margin: 1 }}
          onClick={() => navigate("/mng/event_register", { state: {user_id: userId} })}
        >
          イベント新規登録
        </Button>

        {/* ユーザー管理画面遷移ボタン */}
        <Button
          variant="contained"
          color="primary"
          sx={{ margin: 1 }}
          onClick={() => navigate("/mng/user", { state: {user_id: userId} })}
        >
          ユーザーマスタ
        </Button>

        {/* 体育館管理画面遷移ボタン */}
        <Button
          variant="contained"
          color="primary"
          sx={{ margin: 1 }}
          onClick={() => navigate("/mng/arena", { state: {user_id: userId} })}
        >
          体育館マスタ
        </Button>

        {/* イベント一覧テーブル */}
        <TableContainer component={Paper}>
            <Table sx={{ tableLayout: 'fixed', display: isMobile ? 'grid' : '' }}>
                <TableHead>
                    <TableRow>
                        <TableCell />
                        <TableCell
                            onClick={() => handleSort('event_name')}
                            sx={{ cursor: 'pointer' }}
                        >
                            イベント名
                            {sortConfig.key === 'event_name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                        </TableCell>
                        <TableCell
                            onClick={() => handleSort('event_date')}
                            sx={{ cursor: 'pointer' }}
                        >
                            開催日
                            {sortConfig.key === 'event_date' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                        </TableCell>
                        <TableCell
                            onClick={() => handleSort('b_start_time')}
                            sx={{ cursor: 'pointer' }}
                        >
                            バスケ
                            {sortConfig.key === 'b_start_time' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                        </TableCell>
                        <TableCell
                            onClick={() => handleSort('d_start_time')}
                            sx={{ cursor: 'pointer' }}
                        >
                            飲み会
                            {sortConfig.key === 'd_start_time' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                        </TableCell>
                        <TableCell
                            onClick={() => handleSort('b_entry_fee')}
                            sx={{ cursor: 'pointer' }}
                        >
                            参加費<br />(バスケ)
                            {sortConfig.key === 'b_entry_fee' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                        </TableCell>
                        <TableCell
                            onClick={() => handleSort('d_entry_fee')}
                            sx={{ cursor: 'pointer' }}
                        >
                            参加費<br />(飲み会)
                            {sortConfig.key === 'd_entry_fee' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                        </TableCell>
                        <TableCell
                            onClick={() => handleSort('arena_name')}
                            sx={{ cursor: 'pointer' }}
                        >
                            場所
                            {sortConfig.key === 'arena_name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                        </TableCell>
                        <TableCell
                            onClick={() => handleSort('restaurant')}
                            sx={{ cursor: 'pointer' }}
                        >
                            飲み会場
                            {sortConfig.key === 'restaurant' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                        </TableCell>
                        <TableCell
                            onClick={() => handleSort('participants_num')}
                            sx={{ cursor: 'pointer' }}
                        >
                            人数
                            {sortConfig.key === 'participants_num' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                        </TableCell>
                        <TableCell
                            onClick={() => handleSort('public_flag')}
                            sx={{ cursor: 'pointer' }}
                        >
                            状態
                            {sortConfig.key === 'public_flag' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                        </TableCell>
                        <TableCell
                            onClick={() => handleSort('cash_flow')}
                            sx={{ cursor: 'pointer' }}
                        >
                            収支
                            {sortConfig.key === 'cash_flow' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {filteredData.map((event) => (
                        <TableRow key={event.event_id}>
                            <TableCell>
                                {Number(event.unapproved_number) > 0 ? (
                                  <Badge
                                    badgeContent={event.unapproved_number}
                                    color="warning"
                                  />
                                  ) : <></>}
                                <IconButton
                                    color="primary"
                                    onClick={() => navigate(`/mng/event`, { state: { user_id: userId, event_id: event.event_id } })}
                                    sx={{
                                        transition: 'transform 0.3s ease',
                                        '&:hover': {
                                            transform: 'translateX(10px)',
                                        },
                                    }}
                                >
                                    <NavigateNext />
                                </IconButton>
                            </TableCell>
                            <TableCell>{event.event_name}</TableCell>
                            <TableCell>{event.event_date}</TableCell>
                            <TableCell>{event.b_start_time} - {event.b_end_time}</TableCell>
                            <TableCell>{event.d_start_time} - {event.d_end_time}</TableCell>
                            <TableCell>{event.b_entry_fee}</TableCell>
                            <TableCell>{event.d_entry_fee}</TableCell>
                            <TableCell>{event.arena_name}</TableCell>
                            <TableCell><a href={event.restaurant_url}>{event.restaurant}</a></TableCell>
                            <TableCell>{event.participants_num}人 / {event.capacity_num}人</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                              <Typography
                                sx={{
                                  padding: '6px 12px',
                                  borderRadius: '8px',
                                  backgroundColor: event.public_flag === '1' ? '#f8c2c2' : '#c2d9f8',
                                  color: event.public_flag === '1' ? 'red' : 'blue',
                                }}
                              >
                                {event.public_flag === '1' ? '公開中' : '非公開'}
                              </Typography>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ color:parseInt(event.cash_flow) >= 0 ?  'blue' : 'red' }}>{parseInt(event.cash_flow) >= 0 ?  '+' : ''}{event.cash_flow}円</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>

        {/* 収支ダイアログ */}
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
          <DialogTitle>収支合計</DialogTitle>
          <DialogContent>
            <Typography variant="h6">{`合計収支: ${totalCashFlow} 円`}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)} color="primary">
              閉じる
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
};

